import { distance } from '@turf/distance'
import React from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import { fade } from 'common_lib/services/color'
import { getStyleProperty } from 'mobile_web/modules/platform'
import strings from '../../../strings'
import { Button, Text } from '@mytours/components'

interface Props {
	route: [number, number, number][]
	defaultIndex?: number
	isOpen?: boolean
	onMouseMove?: (coords: [number, number]) => void
	onMouseLeave?: () => void
	onToggle?: () => void
}

const ElevationGraph: React.FC<Props> = ({
	route,
	defaultIndex,
	isOpen = false,
	onMouseMove,
	onMouseLeave,
	onToggle,
}) => {
	let lastPoint = route[0]
	let dist = 0
	let maxEle = 0
	const data = route.map(currentPoint => {
		dist += distance(lastPoint, currentPoint, { units: 'meters' })
		lastPoint = currentPoint

		if (currentPoint[2] > maxEle) {
			maxEle = currentPoint[2]
		}

		return {
			dist: distanceFormatter(dist),
			ele: currentPoint[2],
			coords: lastPoint,
		}
	})

	const CustomTooltip = ({ active, payload, label }: any) => {
		if (active && payload && payload.length) {
			return (
				<div className="elevation-graph__tooltip">
					<Text variant="inherit">{strings.formatString(strings.distance, { distance: label })}</Text>
					<br />
					<Text variant="inherit">
						{strings.formatString(strings.elevation, { elevation: distanceFormatter(payload[0].value, 'm') })}
					</Text>
				</div>
			)
		}
		return null
	}

	const bodyColor = getStyleProperty('--body-color')
	const gridColor = getStyleProperty('--body-color-hover')

	return (
		<div className={`elevation-graph ${isOpen ? 'elevation-graph-show' : ''}`}>
			<div className="elevation-graph__header">
				<div className="elevation-graph__info">
					<div>
						<Text variant="inherit">{strings.route_distance}</Text>
						<br />
						<Text variant="inherit" className="elevation-graph__info__value">
							{distanceFormatter(dist)}
						</Text>
					</div>
					<div>
						<Text variant="inherit">{strings.max_elevation}</Text>
						<br />
						<Text variant="inherit" className="elevation-graph__info__value">
							{distanceFormatter(maxEle, 'm')}
						</Text>
					</div>
				</div>

				<Button
					variant="clear"
					onClick={onToggle}
					className="elevation-graph__toggle-button"
					aria-label={isOpen ? strings.collapse : strings.expand}
				>
					<i className={isOpen ? 'fas fa-angle-down' : 'fas fa-angle-up'} aria-hidden={true} />
				</Button>
			</div>

			<div className="elevation-graph__graph">
				<ResponsiveContainer>
					<AreaChart
						data={data}
						onMouseMove={e => e.activePayload?.[0].payload.coords && onMouseMove?.(e.activePayload[0].payload.coords)}
						onMouseLeave={onMouseLeave}
					>
						<Area type="monotone" dataKey="ele" stroke={bodyColor} strokeWidth={2} fill={fade(bodyColor, 0.7)} />
						<CartesianGrid stroke={gridColor} vertical={false} strokeDasharray="5 5" />
						<XAxis dataKey="dist" stroke={gridColor} minTickGap={40} />
						<YAxis
							unit="m"
							stroke={gridColor}
							domain={[(dataMin: number) => dataMin - (maxEle - dataMin) * 0.05, 'auto']}
						/>
						<Tooltip content={CustomTooltip} defaultIndex={defaultIndex} cursor={{ stroke: bodyColor }} />
					</AreaChart>
				</ResponsiveContainer>
			</div>
		</div>
	)
}

const distanceFormatter = (distance: number, unit: 'auto' | 'm' = 'auto') => {
	if (distance < 1000 || unit === 'm') {
		return `${distance.toFixed()} m`
	}
	return `${(distance / 1000).toFixed(1)} km`
}

export { ElevationGraph }
